import {
  Target,
  RefreshCw,
  Shuffle,
  Activity,
  MessageSquare,
  Radar,
  UserPlus,
  BarChart3,
  Workflow,
} from "lucide-react";
import {
  COMMERCIAL_AGENTS,
  ACTIVATION_STEPS,
  ECOSYSTEM_TIERS,
  VISION_TONE,
} from "../../config/commercialVision";
import { T } from "../../lib/testids";

const ICONS = {
  target: Target,
  "refresh-cw": RefreshCw,
  shuffle: Shuffle,
  activity: Activity,
  "message-square": MessageSquare,
  radar: Radar,
  "user-plus": UserPlus,
  "bar-chart-3": BarChart3,
  workflow: Workflow,
};

const TONE_CLASSES = {
  [VISION_TONE.active]: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
  [VISION_TONE.pending]: "border-amber-500/30 bg-amber-500/10 text-amber-400",
  [VISION_TONE.soon]: "border-zinc-600/40 bg-zinc-700/10 text-zinc-400",
};

/**
 * Solo escritorio (lg+). No es un dashboard alternativo ni una segunda Home:
 * es una sección informativa, no interactiva, que aparece DESPUÉS del
 * trabajo del día — la misma Home, los mismos datos, solo con más espacio
 * para mostrar hacia dónde va la plataforma. En móvil no se renderiza.
 */
export default function CommercialVisionSection() {
  return (
    <div
      data-testid={T.homeVisionSection}
      className="hidden lg:block border-t border-white/[0.06] bg-[#0a0e18] px-8 xl:px-14 py-16"
    >
      <div className="max-w-[960px] mx-auto">
        <div className="text-[11px] text-zinc-500 font-mono uppercase tracking-[0.25em] mb-3">
          Visión de plataforma
        </div>
        <div className="font-display font-black text-2xl xl:text-3xl text-zinc-50">
          Tu operación comercial en evolución
        </div>
        <p className="text-[15px] text-zinc-400 mt-2 max-w-2xl leading-relaxed">
          Commercial Operator reúne distintos especialistas digitales que analizan,
          priorizan y organizan el trabajo comercial.
        </p>

        <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 mt-9">
          {COMMERCIAL_AGENTS.map((agent) => {
            const Icon = ICONS[agent.icon];
            return (
              <div
                key={agent.id}
                data-testid={T.homeVisionCard(agent.id)}
                className="rounded-2xl bg-white/[0.025] hover:bg-white/[0.04] transition-colors p-5"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-white/[0.05] flex items-center justify-center shrink-0">
                    <Icon size={15} className="text-zinc-400" />
                  </div>
                  <span
                    className={`text-[9px] font-mono uppercase tracking-[0.12em] px-2 py-1 rounded-full shrink-0 ${TONE_CLASSES[agent.tone]}`}
                  >
                    {agent.status}
                  </span>
                </div>
                <div className="font-display font-bold text-sm text-zinc-100">{agent.title}</div>
                <p className="text-xs text-zinc-500 mt-1.5 leading-relaxed">{agent.description}</p>
              </div>
            );
          })}
        </div>

        <div
          data-testid={T.homeActivationPanel}
          className="mt-10 rounded-3xl bg-zinc-50 text-[#0d1321] p-7 xl:p-9 max-w-4xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.5)]"
        >
          <div className="text-[10px] text-amber-600 font-mono uppercase tracking-[0.25em] mb-2 font-semibold">
            Próximo paso, contigo
          </div>
          <div className="font-display font-black text-xl text-[#0d1321]">
            Lo que vamos a activar contigo
          </div>
          <p className="text-sm text-zinc-700 mt-3 leading-relaxed max-w-2xl">
            Renovaciones ya está disponible. La siguiente etapa será conectar tu WhatsApp
            empresarial y poner en marcha una campaña real de Siigo Fiscal.
          </p>
          <ol className="mt-4 space-y-2">
            {ACTIVATION_STEPS.map((step, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-zinc-800">
                <span className="font-mono text-xs text-amber-600 font-bold shrink-0 mt-0.5">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {step}
              </li>
            ))}
          </ol>
          <p className="text-xs text-zinc-500 mt-4 leading-relaxed">
            Todavía no está conectado y no hay mensajes automáticos activos. Se activa
            acompañado, contigo — es el siguiente paso concreto, no una promesa abstracta.
          </p>
        </div>

        {/* Discreto a propósito: no compite con la acción principal, no es
            un marketplace, no lista integraciones activas. Es la sensación
            de que Commercial Operator no vive aislado. */}
        <div
          data-testid={T.homeEcosystemSection}
          className="mt-14 pt-10 border-t border-white/[0.06] max-w-4xl"
        >
          <div className="font-display font-bold text-lg text-zinc-300">
            Ecosistema en evolución
          </div>
          <p className="text-sm text-zinc-500 mt-1.5 max-w-2xl leading-relaxed">
            Commercial Operator está diseñado para crecer junto con las herramientas
            que ya utilizan distribuidores y PYMEs.
          </p>
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-x-8 gap-y-5 mt-6">
            {ECOSYSTEM_TIERS.map((tier) => (
              <div key={tier.id}>
                <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-zinc-600 mb-2">
                  {tier.label}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {tier.items.map((item) => (
                    <span
                      key={item}
                      className="text-[11px] text-zinc-400 bg-white/[0.04] rounded-full px-2.5 py-1"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
